import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../components/elements/Card';
import * as Unicons from '@iconscout/react-unicons';
import { useHistory, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import ToolBox from '../../components/elements/ToolBox';
import DataTables from '../../components/elements/DataTables';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import CreateMission from './CreateMission';
import {
  deleteMasterMission,
  editMasterMission,
  getMasterMission,
  postMasterMission
} from '../../services/mission';
import dayjs from 'dayjs';
import { renderDate } from '../../utilities/renderDate';
import ModalDelete from '../../components/fragments/ModalDelete/ModalDelete';
import { convertToLocalTime } from '../../utilities/convertToLocaleTime';

const columns = [
  {
    heading: 'Mission Name',
    props: 'missionName',
    id: 'missionName',
    sortable: true
  },
  {
    heading: 'Mission Type',
    props: 'missionType',
    id: 'missionType',
    sortable: true
  },
  {
    heading: 'Mission Category',
    props: 'missionCategory',
    id: 'missionCategory',
    sortable: true
  },
  {
    heading: 'Reward Point',
    props: 'rewardPoint',
    id: 'rewardPoint',
    sortable: true
  },
  {
    heading: 'Reward XP',
    props: 'rewardXP',
    id: 'rewardXP',
    sortable: true
  },
  {
    heading: 'Active Period',
    props: (item) => renderActivePeriod(item),
    id: 'startDate',
    sortable: true
  },
  {
    heading: 'Action',
    props: 'action',
    sortable: false
  }
];

const renderActivePeriod = (item) => {
  const startAt = item.startDate + 'Z';
  const endAt = item.endDate + 'Z';
  const startDate = dayjs(startAt);
  const endDate = dayjs(endAt);

  if (startDate.year() === endDate.year()) {
    return (
      <>
        {renderDate(startAt, 'datetime')} -<br />
        {renderDate(endAt, 'DD MMMM YYYY, HH:mm')}
      </>
    );
  }

  return (
    <>
      {renderDate(startAt, 'datetimeyear')} -<br />
      {renderDate(endAt, 'datetimeyear')}
    </>
  );
};

const MasterMissionPage = (props) => {
  const history = useHistory();
  const location = useLocation();

  const queryParams: any = new URLSearchParams(location.search);

  const paramsObject = {
    search: queryParams.get('search'),
    page: queryParams.get('page'),
    size: queryParams.get('size')
  };

  const [modalConfig, setModalConfig] = useState({
    show: false,
    data: null,
    type: ''
  });

  const [filter, setFilter] = useState({
    search: '',
    page: 1,
    size: 10
  });

  // Queries
  const fetchMasterMission = useQuery({
    queryKey: ['getMasterMission'],
    queryFn: () =>
      getMasterMission({
        search: filter.search,
        page: filter.page,
        size: filter.size
      })
  });

  const createMasterMission = useMutation({
    mutationFn: postMasterMission,
    onSuccess: () => {
      fetchMasterMission.refetch();
      handleCloseModal();
      showNotification({
        type: 'success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const updateMasterLevel = useMutation({
    mutationFn: editMasterMission,
    onSuccess: () => {
      fetchMasterMission.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Update Master Mission',
        message: 'Updated data will be show in the table'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const removeMasterMission = useMutation({
    mutationFn: deleteMasterMission,
    onSuccess: () => {
      fetchMasterMission.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Delete Master Mission',
        message: 'Delete Master Mission success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  useEffect(() => {
    if (filter?.search || filter?.page || filter?.size) {
      fetchMasterMission.refetch();
    }
  }, [filter]);

  useEffect(() => {
    if (paramsObject?.search || paramsObject?.page || paramsObject?.size) {
      setFilter({
        search: paramsObject?.search || '',
        page: paramsObject?.page || 1,
        size: paramsObject?.size || 10
      });
    }
  }, [paramsObject?.search, paramsObject?.page, paramsObject?.size]);

  const buttonOnClick = () => {
    history.push('/management/mission/add');
  };

  const handleSearch = useCallback((data: any) => {
    history.push({
      pathname: '/management/mission',
      search: `?search=${data}&page=1`
    });
  }, []);

  const handleCloseModal = () => {
    setModalConfig({
      show: false,
      data: null,
      type: ''
    });
  };

  const handleDeleteData = (data) => {
    removeMasterMission.mutate(data);
  };

  const handleSubmitData = (data) => {
    const payload = {
      ...data,
      userIn:
        modalConfig.type === 'edit'
          ? modalConfig.data.userIn
          : props.auth.binusianId,
      userUp: modalConfig.type === 'edit' ? props.auth.binusianId : ''
    };

    if (modalConfig.type === 'edit') {
      updateMasterLevel.mutate({
        ...payload,
        missionID: modalConfig.data.missionID
      });
      return;
    }
    createMasterMission.mutate(payload);
    return;
  };
  return (
    <AdminLayout>
      <Breadcrumbs
        pageTitle="Mission"
        itemList={[
          {
            title: 'Back Office BCL&D',
            href: '#',
            icon: <Unicons.UilFileCopyAlt size={16} />
          },
          {
            title: 'Reward & Gamification',
            href: '#'
          },
          {
            title: 'Mission',
            href: '/management/master-mission'
          }
        ]}
      />
      <Card>
        <ToolBox
          handleSearch={handleSearch}
          buttonOnClick={buttonOnClick}
          buttonText="Create Mission"
        />
        <DataTables
          columns={columns}
          loading={fetchMasterMission?.isLoading}
          data={fetchMasterMission?.data?.data?.data || []}
          handleDelete={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'delete'
            });
          }}
          handleEdit={(data) => {
            history.push('/management/mission/edit/' + data.missionID);
          }}
        />
      </Card>

      <CreateMission
        isVisible={
          modalConfig.show &&
          (modalConfig.type === 'add' || modalConfig.type === 'edit')
        }
        type={modalConfig.type}
        // data={modalConfig.data}
        data={{
          ...modalConfig.data,
          status:
            modalConfig.type === 'edit'
              ? modalConfig.data.status === 'Y'
                ? 'Active'
                : 'InActive'
              : ''
        }}
        onClose={handleCloseModal}
        onSubmit={handleSubmitData}
        loading={createMasterMission?.isLoading}
      />

      <ModalDelete
        title="Master Mission"
        isVisible={modalConfig.show && modalConfig.type === 'delete'}
        loading={removeMasterMission.isLoading}
        onClose={handleCloseModal}
        onConfirmation={() =>
          handleDeleteData({
            missionID: modalConfig.data.missionID,
            userUp: props.auth.binusianId
          })
        }
      />
    </AdminLayout>
  );
};

const convertDate = (date: string) => {
  const newDate = new Date(date);
  // Add 7 hours to adjust for UTC+7
  newDate.setTime(newDate.getTime() + 7 * 60 * 60 * 1000);

  return newDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export default MasterMissionPage;
